import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import gql from 'graphql-tag';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MeetingFieldsFragment,
  MeetingRelationClientsFragment,
  useMeetingUserAccessListViewQuery,
} from '@/@generated/codegen/API';
import ClientTag from '@/components/ClientTag/ClientTag';
import useMeetingUserAccessForMeeting from '@/hooks/useMeetingUserAccessForMeeting';
import { AppSpinner } from '@/ui/AppSpinner';
import { captureSentry } from '@/utils/helpers/sentryHelper';

type Props = {
  meeting: (MeetingFieldsFragment & MeetingRelationClientsFragment) | undefined;
  onClose: () => void;
};

gql`
  query MeetingUserAccessListView($paginationArgs: PaginationArgs!, $where: UserWhereInput) {
    listUsers(paginationArgs: $paginationArgs, where: $where) {
      edges {
        node {
          ...UserFields
          ...UserRelationClient
        }
      }
    }
  }
`;

const MeetingUserAccessList = ({ meeting, onClose }: Props) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const [realtimeSearchFilter, setRealtimeSearchFilter] = useState<string | undefined>();
  const debounced = debounce(setSearchFilter, 500);
  const debouncedSearchFilterOnChange = useCallback(debounced, []);
  const userListQuery = useMeetingUserAccessListViewQuery({
    variables: {
      paginationArgs: {
        first: 20,
      },
      where: {
        isGuest: {
          equals: false,
        },
        isMocked: {
          equals: false,
        },
        email: {
          contains: searchFilter,
        },
      },
    },
  });
  const userList = useMemo(() => {
    return userListQuery.data?.listUsers?.edges.map((edge) => edge.node) || [];
  }, [userListQuery.data]);
  const {
    userAccessList,
    giveUserAccess,
    deleteAccess,
    isLoading: isMeetingAccessLoading,
  } = useMeetingUserAccessForMeeting(meeting?.id);
  const isLoading = userListQuery.loading || isUpdating || isMeetingAccessLoading;

  // --------------------- handlers ------------------------

  const onUserAccessChange = async (userId: string, value: boolean) => {
    setIsUpdating(true);
    if (value) {
      await giveUserAccess(userId);
    } else {
      const userMeetingAccess = userAccessList.find(
        (el) => el.userId === userId && el.meetingId === meeting?.id
      );
      if (!userMeetingAccess) {
        captureSentry({
          title: 'User meeting access not found but trying to delete it',
        });
        return;
      }
      await deleteAccess(userMeetingAccess.id);
    }
    //NOTE: doing this because otherwise it doesn't look good on the UI
    setTimeout(() => {
      setIsUpdating(false);
    }, 200);
  };

  // --------------------- render ------------------------
  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer isOpen={meeting !== undefined} placement="right" size="md" onClose={handleClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <Heading pb="10px" size="md">
            {meeting?.name}
          </Heading>
          <Input
            placeholder={t('admin.meetings.list.userAccess.search.user') || undefined}
            size="sm"
            value={realtimeSearchFilter}
            onChange={(target) => {
              setRealtimeSearchFilter(target.currentTarget.value);
              debouncedSearchFilterOnChange(target.currentTarget.value);
            }}
          />
        </DrawerHeader>
        <DrawerBody>
          <VStack p="10px" align="left" divider={<Divider />}>
            {(isMeetingAccessLoading || userListQuery.loading) && <AppSpinner />}
            {!isMeetingAccessLoading &&
              !userListQuery.loading &&
              userList.map((item) => {
                // NOTE: Keeping it like this for future proofing, in case we allow users to be part
                // of multiple clients
                const userGroupsWithAccess = [item.client]
                  .filter((el) => meeting?.clients?.edges.some((c) => c.node.id === el?.id))
                  .map((el) => el?.name);
                const userHasGroupAccess = userGroupsWithAccess.length > 0;
                const userMeetingAccess = userAccessList?.find(
                  (el) => el.userId === item.id && el.meetingId === meeting?.id
                );
                return (
                  <HStack
                    alignItems="center"
                    justifyContent="left"
                    key={item.id}
                    opacity={userHasGroupAccess ? 0.5 : 1}
                  >
                    <Checkbox
                      size="lg"
                      isDisabled={userHasGroupAccess}
                      isChecked={userHasGroupAccess || !!userMeetingAccess ? true : false}
                      onChange={(e) => void onUserAccessChange(item.id, e.currentTarget.checked)}
                    />
                    <Text userSelect="none">{item.email} </Text>
                    {userHasGroupAccess && <ClientTag client={userGroupsWithAccess.join(', ')} />}
                  </HStack>
                );
              })}
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <Button isLoading={isLoading} variant="outline" mr={3} onClick={handleClose} w="full">
            Exit
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MeetingUserAccessList;
